import React, { useState, useEffect } from 'react';
import { Form, Input, Select, Tag, Space, Typography, Button, message } from 'antd';
import { SaveOutlined } from '@ant-design/icons';
import type { ProcessFuncCodeDetailDto, FunctionLanguage } from '../../../../api/opmApi';
import { OPMApi } from '../../../../api/opmApi';

const { Text } = Typography;

interface BasicInfoTabProps {
  definition: ProcessFuncCodeDetailDto | null;
  ontologyId: number;
  processNodeId: string;
  onDefinitionUpdate: (def: ProcessFuncCodeDetailDto) => void;
}

const STATUS_MAP: Record<string, { color: string; label: string }> = {
  DRAFT: { color: 'default', label: '草稿' },
  VALID: { color: 'success', label: '有效' },
  INVALID: { color: 'error', label: '无效' },
  DEPLOYED: { color: 'blue', label: '已部署' },
};

const PROCESS_TYPE_OPTIONS = [
  { label: '计算函数', value: 'FUNCTION' },
  { label: '异步任务', value: 'JOB', disabled: true },
  { label: '工具调用', value: 'TOOLS', disabled: true },
  { label: '外部接口', value: 'API', disabled: true },
];

const LANGUAGE_OPTIONS = [
  { label: 'TypeScript', value: 'TYPESCRIPT' as FunctionLanguage },
  { label: 'Python', value: 'PYTHON' as FunctionLanguage },
];

const BasicInfoTab: React.FC<BasicInfoTabProps> = ({
  definition,
  ontologyId,
  processNodeId,
  onDefinitionUpdate,
}) => {
  const [language, setLanguage] = useState<FunctionLanguage>('TYPESCRIPT');
  const [processType, setProcessType] = useState<string>('FUNCTION');
  const [saving, setSaving] = useState(false);
  const [dirty, setDirty] = useState(false);

  useEffect(() => {
    if (definition) {
      setLanguage(definition.language || 'TYPESCRIPT');
      setProcessType(definition.type || 'FUNCTION');
      setDirty(false);
    }
  }, [definition]);

  const handleSave = async () => {
    if (!processNodeId) return;
    setSaving(true);
    try {
      await OPMApi.saveProcessInfo(ontologyId, processNodeId, {
        language,
        processType,
      });
      if (definition) {
        onDefinitionUpdate({ ...definition, language, type: processType });
      }
      setDirty(false);
      message.success('保存成功');
    } catch (error: any) {
      message.error(error.message || '保存失败');
    } finally {
      setSaving(false);
    }
  };

  if (!definition) {
    return (
      <div style={{ padding: 40, textAlign: 'center', color: '#999' }}>
        暂无 Process 定义数据
      </div>
    );
  }

  const statusInfo = STATUS_MAP[definition.status] || { color: 'default', label: definition.status };

  return (
    <div style={{ maxWidth: 640, margin: '0 auto', padding: '16px 0' }}>
      <Form layout="vertical" colon={false}>
        <Form.Item label="Process 名称">
          <Input value={definition.processName} readOnly variant="filled" />
        </Form.Item>

        <Form.Item label="函数名称">
          <Input
            value={definition.functionName}
            readOnly
            variant="filled"
            style={{ fontFamily: 'monospace' }}
          />
        </Form.Item>

        <Form.Item label="节点 ID">
          <Input
            value={definition.processNodeId}
            readOnly
            variant="filled"
            style={{ fontFamily: 'monospace', fontSize: 12 }}
          />
        </Form.Item>

        <Form.Item label="过程类型">
          <Select
            value={processType}
            options={PROCESS_TYPE_OPTIONS}
            onChange={v => { setProcessType(v); setDirty(true); }}
            style={{ width: 200 }}
          />
        </Form.Item>

        <Form.Item label="编程语言">
          <Select
            value={language}
            options={LANGUAGE_OPTIONS}
            onChange={v => { setLanguage(v); setDirty(true); }}
            style={{ width: 200 }}
          />
        </Form.Item>

        <Form.Item label="状态">
          <Tag color={statusInfo.color}>{statusInfo.label}</Tag>
        </Form.Item>

        <Form.Item label="时间信息">
          <Space direction="vertical" size={4}>
            <Text type="secondary" style={{ fontSize: 13 }}>
              创建时间：{definition.createdTime || '-'}
            </Text>
            <Text type="secondary" style={{ fontSize: 13 }}>
              更新时间：{definition.updatedTime || '-'}
            </Text>
          </Space>
        </Form.Item>

        {dirty && (
          <Form.Item>
            <Button
              type="primary"
              icon={<SaveOutlined />}
              onClick={handleSave}
              loading={saving}
            >
              保存
            </Button>
          </Form.Item>
        )}
      </Form>
    </div>
  );
};

export default BasicInfoTab;
